import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@vtf/shared';
import { api, errorText, setSession, type SessionUser } from '../lib/api';
import { Button, Card, cx, ErrorText, Field, Input } from '../components/ui';
import { PublicFrame, TimezoneSelect } from './Apply';

/**
 * INT-ENR-03: the one-time activation link from the approval email. Set a password and confirm the timezone, then
 * the normal mandatory 2FA enrolment (auth module), then the floor documents (PolicyGate) and Today.
 */

interface ActivationView {
  name: string;
  email: string;
  timezone: string;
  expiresAt: string;
}
type Enrol = { step: 'ENROL'; challenge: string; otpauthUrl: string; secret: string; qrDataUrl: string } | { step: 'TOTP'; challenge: string };
type SessionResp = { accessToken: string; user: SessionUser; recoveryCodes?: string[] };

const STEPS = ['Password', 'Two-step sign-in', 'Floor documents', 'Today'];

function Progress({ at }: { at: number }) {
  return (
    <ol aria-label="Getting set up" className="m-0 grid list-none grid-cols-4 gap-2 p-0">
      {STEPS.map((s, i) => (
        <li key={s} aria-current={i === at ? 'step' : undefined} className="flex flex-col gap-1.5">
          <span className={cx('h-1.5 rounded', i <= at ? 'bg-accent' : 'bg-[#232a34]')} />
          <span className={cx('text-xs', i === at ? 'font-semibold text-ink' : i < at ? 'text-sub' : 'text-muted')}>{s}</span>
        </li>
      ))}
    </ol>
  );
}

export function Activate() {
  const { token } = useParams();
  const nav = useNavigate();
  const view = useQuery({ queryKey: ['activation', token], queryFn: () => api<ActivationView>(`/activation/${token}`), retry: false, staleTime: Infinity });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [timezone, setTimezone] = useState('');
  const [enrol, setEnrol] = useState<Enrol | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState<SessionResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (view.data && !timezone) setTimezone(view.data.timezone);
  }, [view.data, timezone]);

  async function activate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < PASSWORD_MIN_LENGTH) return setError(`Use at least ${PASSWORD_MIN_LENGTH} characters for your password.`);
    if (password !== confirm) return setError('The two passwords don’t match.');
    setBusy(true);
    try {
      setEnrol(await api<Enrol>(`/activation/${token}`, { body: { password, timezone } }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    if (!enrol) return;
    setError(null);
    setBusy(true);
    try {
      const s = await api<SessionResp>(enrol.step === 'ENROL' ? '/auth/2fa/enrol' : '/auth/2fa/verify', { body: { challenge: enrol.challenge, code: code.trim() } });
      if (s.recoveryCodes) setDone(s);
      else {
        setSession(s.accessToken, s.user);
        nav('/');
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const enter = (to: string) => {
    if (!done) return;
    setSession(done.accessToken, done.user);
    nav(to);
  };

  if (view.isLoading) return <PublicFrame><Card className="p-6 text-sub">Loading…</Card></PublicFrame>;
  if (view.error && !enrol)
    return (
      <PublicFrame>
        <Card tone="warn" className="flex flex-col gap-3 p-6">
          <h1 className="m-0 text-lg font-bold">This activation link doesn’t work</h1>
          <p className="m-0 text-sm text-sub">{errorText(view.error)}</p>
          <Link to="/login" className="text-sm">Go to sign in</Link>
        </Card>
      </PublicFrame>
    );

  return (
    <PublicFrame>
      <Card className="flex flex-col gap-5 p-6">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-xl font-bold">Welcome{view.data ? `, ${view.data.name.split(' ')[0]}` : ''}</h1>
          <span className="text-sm text-sub">Let’s get your floor account ready. It takes about two minutes.</span>
        </div>
        <Progress at={done ? 1 : enrol ? 1 : 0} />

        {!enrol && view.data && (
          <form onSubmit={activate} className="flex flex-col gap-4">
            <Field label="Email">
              <Input value={view.data.email} readOnly autoComplete="username" />
            </Field>
            <Field label="Choose a password" hint={`At least ${PASSWORD_MIN_LENGTH} characters. A short sentence is easy to remember and hard to guess.`}>
              <Input type="password" autoComplete="new-password" minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Field label="Type it again">
              <Input type="password" autoComplete="new-password" maxLength={PASSWORD_MAX_LENGTH} required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </Field>
            <Field label="Your timezone" hint="All times in VTF are shown in this zone. You can change it later in Settings.">
              <TimezoneSelect value={timezone} onChange={setTimezone} />
            </Field>
            <ErrorText error={error} />
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Continue'}
            </Button>
          </form>
        )}

        {enrol && !done && (
          <form onSubmit={verify} className="flex flex-col gap-4">
            {enrol.step === 'ENROL' ? (
              <>
                <p className="m-0 text-sm text-sub">Two-step sign-in is required on the floor. Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code it shows.</p>
                <img src={enrol.qrDataUrl} alt="QR code for your authenticator app" className="mx-auto h-44 w-44 rounded-lg bg-white p-2" />
                <span className="text-center font-mono text-xs break-all text-muted" aria-label="Setup key">{enrol.secret}</span>
              </>
            ) : (
              <p className="m-0 text-sm text-sub">Enter the 6-digit code from your authenticator app.</p>
            )}
            <Field label="Code">
              <Input inputMode="numeric" autoComplete="one-time-code" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} mono />
            </Field>
            <ErrorText error={error} />
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Checking…' : 'Turn on two-step sign-in'}
            </Button>
          </form>
        )}

        {done?.recoveryCodes && (
          <div className="flex flex-col gap-3">
            <h2 className="m-0 text-base font-semibold">Save your recovery codes</h2>
            <p className="m-0 text-sm text-sub">Each code works once if you lose your phone. They won’t be shown again.</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-field p-3 font-mono text-sm">
              {done.recoveryCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <Button variant="primary" onClick={() => enter('/')}>
              I’ve saved them, continue
            </Button>
            <p className="m-0 text-xs text-muted">
              Next you’ll read and accept the floor documents. Then link Telegram in{' '}
              <button type="button" className="cursor-pointer p-0 text-xs text-accent" onClick={() => enter('/settings')}>
                Settings
              </button>{' '}
              so report reminders reach your phone.
            </p>
          </div>
        )}
      </Card>
    </PublicFrame>
  );
}
